import { loadStripe } from "@stripe/stripe-js";
import { Button } from "components/buttons/button";
import { CountrySelector } from "components/forms/country-select";
import { StandardInput } from "components/forms/input";
import { Spinner } from "components/spinner";
import { useGetAddressForAccountQuery } from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { useUser } from "lib/hooks/use-user";
import Link from "next/link";
import { SubscriptionCheckoutParams } from "pages/api/stripe/subscription-checkout-session";
import { SubscriptionPageSub } from "pages/shop/subscriptions/[slug]";
import { MouseEvent, useState } from "react";
import currency from "currency.js";
import { Form } from "components/Form";
import { Input } from "components/Form/Input";
import { z } from "zod";
import { FormSubmit } from "components/Form/FormSubmit";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);

interface Props {
  subscription: SubscriptionPageSub;
}

export const SubscriptionAddressForm = ({ subscription }: Props) => {
  const { userData } = useUser();
  const [shouldUseNewAddress, setShouldUseNewAddress] =
    useState<boolean>(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const { data, loading } = useGetAddressForAccountQuery({
    variables: {
      uuid: userData?.accountId,
    },
    skip: !userData?.accountId,
  });

  const defaultAddress = data?.accounts_by_pk.primary_address;
  const canUseDefaultAddress =
    !!data?.accounts_by_pk?.primary_address?.contact_email &&
    !!data?.accounts_by_pk?.primary_address?.phone_number &&
    !!data?.accounts_by_pk?.primary_address?.line_1 &&
    !!data?.accounts_by_pk?.primary_address?.city &&
    !!data?.accounts_by_pk?.primary_address?.code;

  if (loading) {
    return <Spinner />;
  }

  if (!loading && !data) {
    return (
      <div className='mt-2 text-sm'>
        Please{" "}
        <Link href='/signin'>
          <a className='underline text-green-dark hover:text-red-dark'>
            Sign in to subscribe
          </a>
        </Link>
      </div>
    );
  }

  const schema = z.object({
    name: z
      .string()
      .min(1, "Please enter your name")
      .max(50, "Wow! That's a long name!"),
    contact_email: z.string().email("Please enter a valid email address"),
    phone_number: z.string().min(1, "Please enter your phone number"),
    line_1: z.string().min(1, "Please enter your address"),
    line_2: z.string().optional(),
    line_3: z.string().optional(),
    city: z.string().min(1, "Please enter your city"),
    region: z.string().optional(),
    code: z.string().min(3, "Please enter your post code"),
  });

  const handleSubscribe = async (values?: z.infer<typeof schema>) => {
    setSubmitting(true);
    const params: SubscriptionCheckoutParams = {
      account_id: data?.accounts_by_pk.id,
      subscriptionId: subscription.id,
      address: shouldUseNewAddress
        ? {
            name: values.name,
            contact_email: values.contact_email,
            phone_number: values.phone_number,
            line_1: values.line_1,
            line_2: values.line_2,
            line_3: values.line_3,
            city: values.city,
            region: values.region,
            code: values.code,
          }
        : {
            ...data?.accounts_by_pk.primary_address,
          },
    };

    const res = await fetch("/api/stripe/subscription-checkout-session", {
      method: "POST",
      body: JSON.stringify(params),
    }).then((res) => res.json());

    console.log(res);

    const stripe = await stripePromise;

    await stripe.redirectToCheckout({
      sessionId: res.id,
    });
    setSubmitting(false);
  };

  return (
    <div className='bg-white rounded-md -space-y-px flex flex-col'>
      {!!defaultAddress && canUseDefaultAddress && (
        <section className='relative border rounded-tl-md rounded-tr-md p-4 flex flex-row flex-wrap'>
          <input
            className='focus:ring-green-default h-4 w-4 mt-1 text-green-default cursor-pointer border-gray-300'
            type='radio'
            name='shipping-address'
            id='useDefaultShipping'
            checked={!shouldUseNewAddress}
            onChange={(e) => {
              if (e.target.checked) setShouldUseNewAddress(false);
            }}
          />
          <label
            htmlFor='useDefaultShipping'
            className='ml-3 flex flex-col cursor-pointer'>
            <h3 className='font-bold text-sm'>
              Use my default shipping details
            </h3>

            <address className='text-sm text-gray-800'>
              {defaultAddress.name}
              <br />
              {defaultAddress.contact_email}
              <br />
              {defaultAddress.phone_number}
              <br />
              {defaultAddress.line_1}
              <br />
              {defaultAddress.line_2 && (
                <>
                  {defaultAddress.line_2}
                  <br />
                </>
              )}

              {defaultAddress.line_3 && (
                <>
                  {defaultAddress.line_3}
                  <br />
                </>
              )}

              {defaultAddress.city}
              <br />
              {defaultAddress.region && (
                <>
                  {defaultAddress.region}
                  <br />
                </>
              )}

              {defaultAddress.code}
              {defaultAddress.country?.name}
              <br />
            </address>
          </label>
          <div className='w-full mt-4'>
            {!shouldUseNewAddress && (
              <button
                type='button'
                className='px-4 py-2 bg-green-default rounded-lg text-white disabled:bg-gray-200 disabled:text-gray-600 inline-flex space-x-3'
                disabled={isSubmitting || shouldUseNewAddress}
                onClick={() => handleSubscribe()}>
                {isSubmitting && (
                  <span className='my-auto'>
                    <Spinner size={4} />
                  </span>
                )}
                <span>
                  Subscribe for{` `}
                  {currency(subscription.price, { fromCents: true }).format({
                    symbol: "£",
                  })}{" "}
                  / month
                </span>
              </button>
            )}
          </div>
        </section>
      )}
      <section className='relative border p-4 flex flex-wrap'>
        <input
          className='focus:ring-green-default h-4 w-4 mt-1 text-green-default cursor-pointer border-gray-300'
          type='radio'
          name='shipping-address'
          id='useCustomShiping'
          checked={shouldUseNewAddress}
          onChange={(e) => {
            if (e.target.checked) setShouldUseNewAddress(true);
          }}
        />
        <label
          htmlFor='useCustomShiping'
          className='ml-3 flex flex-col cursor-pointer'>
          <h3 className='font-bold text-sm'>New shipping address</h3>
        </label>
        {shouldUseNewAddress && (
          <Form
            className='py-2 grid grid-cols-2 gap-4'
            onSubmit={handleSubscribe}
            schema={schema}>
            <Input
              name='name'
              label='Name'
              type='text'
              className='col-span-full'
            />
            <Input
              name='contact_email'
              label='Email'
              type='email'
              className='col-span-full'
            />
            <Input
              name='phone_number'
              label='Phone'
              type='phone'
              className='col-span-full'
            />
            <Input name='line_1' label='Line 1' type='text' />
            <Input name='line_2' label='Line 2' type='text' />
            <Input name='line_3' label='Line 3' type='text' />
            <Input name='city' label='City' type='text' />
            <Input name='region' label='Region' type='text' />
            <Input name='code' label='Post Code' type='text' />

            <div className='col-span-full'>
              <FormSubmit className='px-4 py-2 bg-green-default rounded-lg text-white disabled:bg-gray-200 disabled:text-gray-600'>
                Subscribe for{` `}
                {currency(subscription.price, { fromCents: true }).format({
                  symbol: "£",
                })}{" "}
                / month
              </FormSubmit>
            </div>
          </Form>
        )}
      </section>
      <section className='pt-2'></section>
    </div>
  );
};
